import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Shield, Mail, MessageSquare } from 'lucide-react';

export type MfaMethod = 'totp' | 'email' | 'sms';

interface MfaMethodSelectorProps {
  onSelect: (method: MfaMethod) => void;
  onCancel?: () => void;
  availableMethods?: MfaMethod[];
  currentMethod?: MfaMethod | null;
}

const METHOD_CONFIG: Record<MfaMethod, { icon: typeof Shield; labelKey: string; descriptionKey: string }> = {
  totp: {
    icon: Shield,
    labelKey: 'mfa.method.totp',
    descriptionKey: 'mfa.method.totpDescription',
  },
  email: {
    icon: Mail,
    labelKey: 'mfa.method.email',
    descriptionKey: 'mfa.method.emailDescription',
  },
  sms: {
    icon: MessageSquare,
    labelKey: 'mfa.method.sms',
    descriptionKey: 'mfa.method.smsDescription',
  },
};

export function MfaMethodSelector({
  onSelect,
  onCancel,
  availableMethods = ['totp', 'email', 'sms'],
  currentMethod = null,
}: MfaMethodSelectorProps) {
  const { t } = useTranslation('common');
  const [selected, setSelected] = useState<MfaMethod | null>(null);

  const handleContinue = () => {
    if (selected) {
      onSelect(selected);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          {t('mfa.chooseMethod', 'Choose verification method')}
        </CardTitle>
        <CardDescription>
          {t('mfa.chooseMethodDescription', 'Select how you want to verify your identity')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <RadioGroup value={selected || ''} onValueChange={(value) => setSelected(value as MfaMethod)}>
          {availableMethods.map((method) => {
            const config = METHOD_CONFIG[method];
            const Icon = config.icon;
            const isCurrentMethod = method === currentMethod;

            return (
              <div
                key={method}
                className={`flex items-center space-x-3 rounded-lg border p-4 cursor-pointer hover:bg-muted/50 transition-colors ${
                  selected === method ? 'border-primary bg-primary/5' : ''
                } ${isCurrentMethod ? 'opacity-50' : ''}`}
                onClick={() => !isCurrentMethod && setSelected(method)}
              >
                <RadioGroupItem value={method} id={method} disabled={isCurrentMethod} />
                <div className="flex items-center gap-3 flex-1">
                  <div className="p-2 rounded-full bg-muted">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor={method} className="cursor-pointer font-medium">
                      {t(config.labelKey, method === 'totp' ? 'Authenticator App' : method === 'email' ? 'Email Code' : 'SMS Code')}
                      {isCurrentMethod && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({t('mfa.currentMethod', 'Current')})
                        </span>
                      )}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        config.descriptionKey,
                        method === 'totp'
                          ? t('common:general.useAnAppLikeGoogleAuthenticator', 'Use an app like Google Authenticator')
                          : method === 'email'
                          ? t('common:general.receiveACodeViaEmail', 'Receive a code via email')
                          : t('common:general.receiveACodeViaSms', 'Receive a code via SMS')
                      )}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </RadioGroup>

        <div className="flex gap-2 pt-4">
          {onCancel && (
            <Button variant="outline" onClick={onCancel} className="flex-1">
              {t('common.cancel', 'Cancel')}
            </Button>
          )}
          <Button onClick={handleContinue} disabled={!selected} className="flex-1">
            {t('common.continue', 'Continue')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
